import { createSlice } from '@reduxjs/toolkit';

// Ensures object maps contain exactly the requested language keys.
function filterMapByIsos(source = {}, isos = []) {
  return (isos || []).reduce((acc, iso) => {
    acc[iso] = source?.[iso] ?? '';
    return acc;
  }, {});
}

// Local draft state for create/update tour flows in map tools.
const tourDraftSlice = createSlice({
  name: 'tourDraft',
  initialState: {
    tourId: '',
    selectedLanguages: [],
    countryId: '',
    cityId: '',
    countryName: '',
    cityName: '',
    buttonTitle: {},
    buttonTitleTranslationIds: {},
    badgeTitle: {},
    badgeTitleTranslationIds: {},
    name: {},
    nameTranslationIds: {},
    description: {},
    descriptionTranslationIds: {},
    duration: {},
    durationTranslationIds: {},
    country: {},
    countryTranslationIds: {},
    region: {},
    regionTranslationIds: {},
    city: {},
    cityTranslationIds: {},
    pointCount: 0,
    stopCount: 0,
    status: 1,
    isActive: true,
    inclusiveAccessType: false,
    multimedia: [],
    banner: null,
    snapshot: null
  },
  reducers: {
    // Ensures each language key exists in all translatable fields.
    ensureTourLanguages(state, action) {
      const isos = (action.payload || []).filter(Boolean);
      state.selectedLanguages = isos.map((iso) => {
        const current = (state.selectedLanguages || []).find(
          (item) => String(item?.iso || '').toLowerCase().trim() === String(iso || '').toLowerCase().trim(),
        );
        return current || {
          id: '',
          language_id: '',
          name: '',
          iso: String(iso || '').toLowerCase().trim(),
        };
      });
      state.buttonTitle = filterMapByIsos(state.buttonTitle, isos);
      state.badgeTitle = filterMapByIsos(state.badgeTitle, isos);
      state.name = filterMapByIsos(state.name, isos);
      state.description = filterMapByIsos(state.description, isos);
      state.duration = filterMapByIsos(state.duration, isos);
      state.buttonTitleTranslationIds = filterMapByIsos(state.buttonTitleTranslationIds, isos);
      state.badgeTitleTranslationIds = filterMapByIsos(state.badgeTitleTranslationIds, isos);
      state.nameTranslationIds = filterMapByIsos(state.nameTranslationIds, isos);
      state.descriptionTranslationIds = filterMapByIsos(state.descriptionTranslationIds, isos);
      state.durationTranslationIds = filterMapByIsos(state.durationTranslationIds, isos);
      state.country = filterMapByIsos(state.country, isos);
      state.region = filterMapByIsos(state.region, isos);
      state.city = filterMapByIsos(state.city, isos);
      state.countryTranslationIds = filterMapByIsos(state.countryTranslationIds, isos);
      state.regionTranslationIds = filterMapByIsos(state.regionTranslationIds, isos);
      state.cityTranslationIds = filterMapByIsos(state.cityTranslationIds, isos);
    },
    setTourSelectedLanguages(state, action) {
      state.selectedLanguages = Array.from(
        new Map(
          (Array.isArray(action.payload) ? action.payload : [])
            .map((item) => {
              const iso = String(item?.iso || '').toLowerCase().trim();
              if (!iso) return null;
              return [
                iso,
                {
                  id: String(item?.id || ''),
                  language_id: String(item?.language_id || ''),
                  name: String(item?.name || ''),
                  iso,
                },
              ];
            })
            .filter(Boolean),
        ).values(),
      );
    },
    // Updates a localized name value.
    setTourNameValue(state, action) {
      const { iso, value } = action.payload || {};
      if (!iso) return;
      state.name[iso] = value ?? '';
    },
    setTourButtonTitleValue(state, action) {
      const { iso, value } = action.payload || {};
      if (!iso) return;
      state.buttonTitle[iso] = value ?? '';
    },
    setTourBadgeTitleValue(state, action) {
      const { iso, value } = action.payload || {};
      if (!iso) return;
      state.badgeTitle[iso] = value ?? '';
    },
    // Updates a localized description HTML value.
    setTourDescriptionValue(state, action) {
      const { iso, value } = action.payload || {};
      if (!iso) return;
      state.description[iso] = value ?? '';
    },
    // Updates a localized duration value.
    setTourDurationValue(state, action) {
      const { iso, value } = action.payload || {};
      if (!iso) return;
      state.duration[iso] = value ?? '';
    },
    // Updates a localized country value.
    setTourCountryValue(state, action) {
      const { iso, value } = action.payload || {};
      if (!iso) return;
      state.country[iso] = value ?? '';
    },
    // Updates a localized region value.
    setTourRegionValue(state, action) {
      const { iso, value } = action.payload || {};
      if (!iso) return;
      state.region[iso] = value ?? '';
    },
    // Updates a localized city value.
    setTourCityValue(state, action) {
      const { iso, value } = action.payload || {};
      if (!iso) return;
      state.city[iso] = value ?? '';
    },
    // Stores selected country/city ids and display labels.
    setTourLocation(state, action) {
      const { countryId = '', cityId = '', countryName = '', cityName = '' } = action.payload || {};
      state.countryId = countryId;
      state.cityId = cityId;
      state.countryName = countryName;
      state.cityName = cityName;
    },
    // Hydrates the full draft when editing an existing tour.
    hydrateTourDraft(state, action) {
      const payload = action.payload || {};
      state.tourId = payload.tourId || payload.id || '';
      state.selectedLanguages = Array.isArray(payload.selectedLanguages)
        ? payload.selectedLanguages
        : Array.isArray(payload.selectedLanguageIsos)
          ? payload.selectedLanguageIsos.map((iso) => ({
              id: '',
              language_id: '',
              name: '',
              iso: String(iso || '').toLowerCase().trim(),
            }))
        : [];
      state.countryId = payload.countryId || '';
      state.countryName = payload.countryName || '';
      state.cityId = payload.cityId || '';
      state.cityName = payload.cityName || '';
      state.buttonTitle = payload.buttonTitle || payload.button_title || {};
      state.buttonTitleTranslationIds = payload.buttonTitleTranslationIds || payload.button_title_translation_ids || {};
      state.badgeTitle = payload.badgeTitle || payload.badge_title || {};
      state.badgeTitleTranslationIds = payload.badgeTitleTranslationIds || payload.badge_title_translation_ids || {};
      state.name = payload.name || {};
      state.nameTranslationIds = payload.nameTranslationIds || {};
      state.description = payload.description || {};
      state.descriptionTranslationIds = payload.descriptionTranslationIds || {};
      state.duration = payload.duration || {};
      state.durationTranslationIds = payload.durationTranslationIds || {};
      state.country = payload.country || {};
      state.countryTranslationIds = payload.countryTranslationIds || {};
      state.region = payload.region || {};
      state.regionTranslationIds = payload.regionTranslationIds || {};
      state.city = payload.city || {};
      state.cityTranslationIds = payload.cityTranslationIds || {};
      state.pointCount = Number(payload.pointCount ?? payload.point_count) || 0;
      state.stopCount = Number(payload.stopCount ?? payload.stop_count) || 0;
      state.status = Number(payload.status) || 1;
      state.isActive = typeof payload.isActive === 'boolean' ? payload.isActive : true;
      state.inclusiveAccessType =
        typeof payload.inclusiveAccessType === 'boolean'
          ? payload.inclusiveAccessType
          : typeof payload.InclussiveAccessType === 'boolean'
            ? payload.InclussiveAccessType
            : false;
      state.multimedia = Array.isArray(payload.multimedia) ? payload.multimedia : [];
      state.banner = payload.banner || null;
      state.snapshot = payload.snapshot || payload.snapShot || payload.tourSnapshot || null;
    },
    // Stores lifecycle status with safe fallback.
    setTourStatus(state, action) {
      const value = Number(action.payload);
      state.status = [1, 2, 3, 4, 5].includes(value) ? value : 1;
    },
    // Stores active/inactive flag.
    setTourIsActive(state, action) {
      state.isActive = Boolean(action.payload);
    },
    setTourInclusiveAccessType(state, action) {
      state.inclusiveAccessType = Boolean(action.payload);
    },
    setTourPointCount(state, action) {
      const value = Number(action.payload);
      state.pointCount = Number.isFinite(value) && value >= 0 ? value : 0;
    },
    setTourStopCount(state, action) {
      const value = Number(action.payload);
      state.stopCount = Number.isFinite(value) && value >= 0 ? value : 0;
    },
    // Clears the full draft after success or manual cancel.
    resetTourDraft(state) {
      state.tourId = '';
      state.selectedLanguages = [];
      state.countryId = '';
      state.cityId = '';
      state.countryName = '';
      state.cityName = '';
      state.buttonTitle = {};
      state.buttonTitleTranslationIds = {};
      state.badgeTitle = {};
      state.badgeTitleTranslationIds = {};
      state.name = {};
      state.nameTranslationIds = {};
      state.description = {};
      state.descriptionTranslationIds = {};
      state.duration = {};
      state.durationTranslationIds = {};
      state.country = {};
      state.countryTranslationIds = {};
      state.region = {};
      state.regionTranslationIds = {};
      state.city = {};
      state.cityTranslationIds = {};
      state.pointCount = 0;
      state.stopCount = 0;
      state.status = 1;
      state.isActive = true;
      state.inclusiveAccessType = false;
      state.multimedia = [];
      state.banner = null;
      state.snapshot = null;
    },
    // Merges uploaded multimedia into draft without duplicates.
    appendTourMultimedia(state, action) {
      const items = Array.isArray(action.payload) ? action.payload : [];
      const mapById = new Map(state.multimedia.map((item) => [item.id, item]));
      items.forEach((item) => {
        if (!item?.id) return;
        const previous = mapById.get(item.id) || {};
        mapById.set(item.id, {
          ...previous,
          ...item,
          sourceId: item.sourceId || previous.sourceId || item.id,
          audioGroupId:
            item.audioGroupId || previous.audioGroupId || item.sourceId || previous.sourceId || item.id,
          languageIso: item.languageIso || previous.languageIso || "",
          mediaRole: item.mediaRole || previous.mediaRole || "",
          audioTitles: item.audioTitles || previous.audioTitles || {}
        });
      });
      state.multimedia = Array.from(mapById.values());
    },
    // Updates one localized title for an audio multimedia item.
    setTourMultimediaAudioTitle(state, action) {
      const { mediaId = '', iso = '', value = '' } = action.payload || {};
      if (!mediaId || !iso) return;
      state.multimedia = state.multimedia.map((item) => {
        if (item.id !== mediaId) return item;
        return {
          ...item,
          audioTitles: {
            ...(item.audioTitles || {}),
            [iso]: value
          }
        };
      });
    },
    // Removes one multimedia item and unsets banner if it points to the same id.
    removeTourMultimedia(state, action) {
      const id = action.payload;
      state.multimedia = state.multimedia.filter((item) => item.id !== id);
      if (state.banner?.id === id) state.banner = null;
    },
    // Stores banner as object shape or null.
    setTourBanner(state, action) {
      const payload = action.payload;
      if (!payload) {
        state.banner = null;
        return;
      }
      if (typeof payload === 'string') {
        state.banner = payload ? { id: payload } : null;
        return;
      }
      state.banner = payload;
    },
    // Stores snapshot image as object shape or null.
    setTourSnapshot(state, action) {
      const payload = action.payload;
      if (!payload) {
        state.snapshot = null;
        return;
      }
      if (typeof payload === 'string') {
        state.snapshot = payload ? { id: payload } : null;
        return;
      }
      state.snapshot = payload;
    }
  }
});

export const {
  ensureTourLanguages,
  setTourSelectedLanguages,
  setTourButtonTitleValue,
  setTourBadgeTitleValue,
  setTourNameValue,
  setTourDescriptionValue,
  setTourDurationValue,
  setTourCountryValue,
  setTourRegionValue,
  setTourCityValue,
  setTourLocation,
  setTourStatus,
  setTourIsActive,
  setTourInclusiveAccessType,
  setTourPointCount,
  setTourStopCount,
  hydrateTourDraft,
  appendTourMultimedia,
  setTourMultimediaAudioTitle,
  removeTourMultimedia,
  setTourBanner,
  setTourSnapshot,
  resetTourDraft
} =
  tourDraftSlice.actions;
export default tourDraftSlice.reducer;
